import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, AlertTriangle, X, ChevronUp } from 'lucide-react';
import apiService from '../../services/ApiService';
import ItemModal from './ItemModal';

// ── Design tokens ──────────────────────────────────────────────────────────
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG        = '#F2F2F7';
const INK       = '#1C1C1E';
const MUTED     = '#8E8E93';
const SECONDARY = '#3C3C43';
const CARD      = { background: '#fff', borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)' };
const INP       = { padding: '11px 14px', border: 'none', borderRadius: 10, fontSize: 15, outline: 'none', background: '#F2F2F7', color: INK, fontFamily: FONT, width: '100%', boxSizing: 'border-box' };
const LBL       = { display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 };
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const SYS_ORANGE = '#FF9500';
const BRAND      = '#84CC16';

const CATEGORIES = ['All', 'Springs', 'Rollers', 'Tracks', 'Cables & Drums', 'Openers', 'Remotes', 'Panels', 'Hardware', 'Tools', 'Other'];

function fmt(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function groupByCategory(items) {
  const groups = {};
  items.forEach(item => {
    const cat = item.category || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  });
  return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function displayName(name) {
  return name === 'Noy' ? 'General' : name;
}

function ActivityCell({ item }) {
  const name = item.updatedByName || item.createdByName || null;
  const date = item.lastUpdated || item.createdAt || null;
  const isCreate = !item.updatedByName && !!item.createdByName;
  if (!name) return <td style={{ padding: '10px 12px', color: MUTED, fontSize: 11 }}>—</td>;
  return (
    <td style={{ padding: '10px 12px' }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: SECONDARY }}>{displayName(name)}</div>
      <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>{isCreate ? 'Added ' : 'Edited '}{fmtDate(date)}</div>
    </td>
  );
}

function SupplierModal({ supplier, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', contactPerson: '', notes: '', ...(supplier || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try { await onSave(form); onClose(); }
    catch (err) { setError(err.message || 'Failed to save.'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.40)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 480, boxShadow: '0 32px 64px rgba(0,0,0,0.22)', fontFamily: FONT }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: INK, margin: 0 }}>{supplier ? 'Edit Supplier' : 'Add Supplier'}</h2>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: '50%', background: BG, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          {error && (
            <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: SYS_RED, fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="sup-name" style={LBL}>Company Name *</label>
            <input id="sup-name" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required style={INP} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label htmlFor="sup-email" style={LBL}>Email</label>
              <input id="sup-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={INP} />
            </div>
            <div>
              <label htmlFor="sup-phone" style={LBL}>Phone</label>
              <input id="sup-phone" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={INP} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="sup-contact" style={LBL}>Contact Person</label>
            <input id="sup-contact" type="text" value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} style={INP} />
          </div>

          <div style={{ marginBottom: 22 }}>
            <label htmlFor="sup-notes" style={LBL}>Notes</label>
            <textarea id="sup-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...INP, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 22px', border: 'none', borderRadius: 10, background: BG, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: INK }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: 10, background: BRAND, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StockAdjustModal({ item, onAdjust, onClose }) {
  const [change, setChange] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const val = parseFloat(change);
    if (isNaN(val) || val === 0) { setError('Enter a non-zero quantity change.'); return; }
    setSaving(true); setError('');
    try { await onAdjust(val, reason); onClose(); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.40)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 380, boxShadow: '0 32px 64px rgba(0,0,0,0.22)', fontFamily: FONT }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: INK, margin: 0 }}>Adjust Stock</h2>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: '50%', background: BG, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: INK, margin: '0 0 4px' }}>{item.name}</p>
          <p style={{ fontSize: 12, color: MUTED, margin: '0 0 18px' }}>Current quantity: <b style={{ color: INK }}>{item.quantity}</b></p>

          {error && (
            <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: SYS_RED, fontSize: 13, marginBottom: 14 }}>{error}</div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="adj-change" style={LBL}>Quantity Change (use negative to remove)</label>
            <input id="adj-change" type="number" step="1" value={change} onChange={e => setChange(e.target.value)} style={INP} placeholder="e.g. +10 or -5" />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label htmlFor="adj-reason" style={LBL}>Reason</label>
            <input id="adj-reason" type="text" value={reason} onChange={e => setReason(e.target.value)} style={INP} placeholder="e.g. Received shipment, Sold, Damaged…" />
          </div>

          {change && !isNaN(parseFloat(change)) && (
            <p style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
              New quantity: <b style={{ color: INK }}>{Math.max(0, item.quantity + parseFloat(change))}</b>
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 22px', border: 'none', borderRadius: 10, background: BG, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: INK }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: 10, background: BRAND, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Apply</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const [tab, setTab] = useState('overview');
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('All');
  const [itemModal, setItemModal] = useState(null); // null | 'new' | item object
  const [stockModal, setStockModal] = useState(null);
  const [supplierModal, setSupplierModal] = useState(null);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, sup] = await Promise.all([apiService.getInventory(), apiService.getSuppliers()]);
      setInventory(inv);
      setSuppliers(sup);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtered items
  const filtered = inventory.filter(i => {
    if (catFilter !== 'All' && i.category !== catFilter) return false;
    if (supplierFilter !== 'All' && i.supplier !== supplierFilter) return false;
    if (stockFilter === 'in-stock' && (i.quantity === 0 || (i.minQuantity > 0 && i.quantity <= i.minQuantity))) return false;
    if (stockFilter === 'low-stock' && !(i.minQuantity > 0 && i.quantity > 0 && i.quantity <= i.minQuantity)) return false;
    if (stockFilter === 'out-of-stock' && i.quantity !== 0) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.side?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const lowStock = inventory.filter(i => i.minQuantity > 0 && i.quantity <= i.minQuantity);
  const outOfStock = inventory.filter(i => i.quantity === 0);
  const totalCostVal = inventory.reduce((s, i) => s + i.quantity * i.costPrice, 0);
  const totalRetailVal = inventory.reduce((s, i) => s + i.quantity * i.sellingPrice, 0);

  // Category breakdown
  const catBreakdown = {};
  inventory.forEach(i => { catBreakdown[i.category] = (catBreakdown[i.category] || 0) + 1; });

  const supplierNames = [...new Set(inventory.map(i => i.supplier).filter(Boolean))];

  async function handleSaveItem(form, imageFile, removeImage) {
    let saved;
    if (itemModal === 'new') {
      saved = await apiService.createItem(form);
      setInventory(prev => [...prev, saved]);
    } else {
      saved = await apiService.updateItem(form.id, form);
      setInventory(prev => prev.map(i => i.id === saved.id ? saved : i));
    }
    if (imageFile) {
      const withImage = await apiService.uploadItemImage(saved.id, imageFile);
      setInventory(prev => prev.map(i => i.id === withImage.id ? withImage : i));
    } else if (removeImage && saved.imagePath) {
      const withoutImage = await apiService.deleteItemImage(saved.id);
      setInventory(prev => prev.map(i => i.id === withoutImage.id ? withoutImage : i));
    }
  }

  async function handleDeleteItem(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    await apiService.deleteItem(item.id);
    setInventory(prev => prev.filter(i => i.id !== item.id));
  }

  async function handleAdjustStock(item, change, reason) {
    const updated = await apiService.adjustStock(item.id, change, reason);
    setInventory(prev => prev.map(i => i.id === updated.id ? updated : i));
  }

  async function handleSaveSupplier(form) {
    if (supplierModal === 'new') {
      const created = await apiService.createSupplier(form);
      setSuppliers(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateSupplier(form.id, form);
      setSuppliers(prev => prev.map(s => s.id === updated.id ? updated : s));
    }
  }

  async function handleDeleteSupplier(supplier) {
    if (!window.confirm(`Delete supplier "${supplier.name}"?`)) return;
    await apiService.deleteSupplier(supplier.id);
    setSuppliers(prev => prev.filter(s => s.id !== supplier.id));
  }

  const tabStyle = (t) => ({
    padding: '10px 20px',
    border: 'none',
    borderBottom: tab === t ? `2px solid ${BRAND}` : '2px solid transparent',
    background: 'none',
    color: tab === t ? BRAND : MUTED,
    fontWeight: tab === t ? 600 : 400,
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: FONT,
    transition: 'color 0.15s',
  });

  if (loading) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, fontFamily: FONT, color: MUTED, fontSize: 15 }}>
      Loading inventory…
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG, fontFamily: FONT }}>
      {error && (
        <div style={{ background: '#FFF1F0', color: SYS_RED, padding: '10px 20px', fontSize: 13, fontFamily: FONT }}>{error}</div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', background: 'white', paddingLeft: 20, flexShrink: 0, borderBottom: `1px solid rgba(0,0,0,0.06)` }}>
        <button style={tabStyle('overview')} onClick={() => setTab('overview')}>Overview</button>
        <button style={tabStyle('products')} onClick={() => setTab('products')}>Products ({inventory.length})</button>
        <button style={tabStyle('suppliers')} onClick={() => setTab('suppliers')}>Suppliers ({suppliers.length})</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 16 : 24 }}>

        {/* ══════════════════════════════════════════════════════════════════
            OVERVIEW TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'overview' && (() => {
          const totalUnits = inventory.reduce((s, i) => s + i.quantity, 0);
          const potentialProfit = totalRetailVal - totalCostVal;
          const avgMargin = totalRetailVal > 0 ? (potentialProfit / totalRetailVal) * 100 : 0;
          const inStock = inventory.filter(i => i.quantity > 0 && !(i.minQuantity > 0 && i.quantity <= i.minQuantity)).length;

          // Value by category
          const catValueMap = {};
          inventory.forEach(i => {
            const cat = i.category || 'Other';
            if (!catValueMap[cat]) catValueMap[cat] = { retail: 0, cost: 0, count: 0 };
            catValueMap[cat].retail += i.quantity * i.sellingPrice;
            catValueMap[cat].cost   += i.quantity * i.costPrice;
            catValueMap[cat].count  += 1;
          });
          const catValueList = Object.entries(catValueMap).sort((a, b) => b[1].retail - a[1].retail);
          const maxCatRetail = catValueList.length > 0 ? catValueList[0][1].retail : 1;

          // Top items by retail value
          const topItems = [...inventory]
            .filter(i => i.quantity > 0 && i.sellingPrice > 0)
            .sort((a, b) => (b.quantity * b.sellingPrice) - (a.quantity * a.sellingPrice))
            .slice(0, 8);

          return (
            <div>
              {/* KPI row 1 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Total Items</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 28px)', fontWeight: 700, color: INK, lineHeight: 1, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inventory.length}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>{totalUnits.toLocaleString()} units in stock</div>
                </div>
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Cost Value</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 28px)', fontWeight: 700, color: SYS_BLUE, lineHeight: 1, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmt(totalCostVal)}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>capital tied up</div>
                </div>
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Retail Value</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 28px)', fontWeight: 700, color: SYS_GREEN, lineHeight: 1, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmt(totalRetailVal)}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>if sold at full price</div>
                </div>
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Potential Profit</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 28px)', fontWeight: 700, color: BRAND, lineHeight: 1, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmt(potentialProfit)}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>{avgMargin.toFixed(1)}% avg margin</div>
                </div>
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Stock Alerts</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 28px)', fontWeight: 700, color: lowStock.length > 0 ? SYS_ORANGE : SYS_GREEN, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lowStock.length}</div>
                  <div style={{ fontSize: 12, color: outOfStock.length > 0 ? SYS_RED : MUTED, marginTop: 6 }}>{outOfStock.length} out of stock</div>
                </div>
              </div>

              {/* Stock health bar */}
              <div style={{ ...CARD, padding: '18px 22px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: INK, margin: 0 }}>Stock Health</h3>
                  <span style={{ fontSize: 11, color: MUTED }}>{inventory.length} SKUs total</span>
                </div>
                <div style={{ height: 10, borderRadius: 5, overflow: 'hidden', background: '#E5E5EA', display: 'flex', marginBottom: 10 }}>
                  {inventory.length > 0 && <>
                    <div style={{ width: `${(inStock / inventory.length) * 100}%`, background: SYS_GREEN, transition: 'width 0.5s' }} />
                    <div style={{ width: `${((lowStock.length - outOfStock.length) / inventory.length) * 100}%`, background: SYS_ORANGE, transition: 'width 0.5s' }} />
                    <div style={{ width: `${(outOfStock.length / inventory.length) * 100}%`, background: SYS_RED, transition: 'width 0.5s' }} />
                  </>}
                </div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  {[
                    { label: 'In Stock', count: inStock, color: SYS_GREEN },
                    { label: 'Low Stock', count: lowStock.length - outOfStock.length, color: SYS_ORANGE },
                    { label: 'Out of Stock', count: outOfStock.length, color: SYS_RED },
                  ].map(({ label, count, color }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: SECONDARY, fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Low stock alerts */}
              {lowStock.length > 0 && (
                <div style={{ ...CARD, padding: '18px 22px', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 14px', color: SYS_ORANGE, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <AlertTriangle size={15} /> Low Stock Alerts
                  </h3>
                  {lowStock.slice(0, 6).map((i, idx) => (
                    <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: idx < Math.min(lowStock.length, 6) - 1 ? '1px solid #F9F9F9' : 'none' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: i.quantity === 0 ? `${SYS_RED}12` : `${SYS_ORANGE}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AlertTriangle size={14} color={i.quantity === 0 ? SYS_RED : SYS_ORANGE} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{i.category}{i.side ? ` · ${i.side}` : ''}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: i.quantity === 0 ? SYS_RED : SYS_ORANGE }}>{i.quantity}</span>
                        <span style={{ fontSize: 11, color: MUTED }}> / {i.minQuantity} min</span>
                      </div>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: i.quantity === 0 ? `${SYS_RED}15` : `${SYS_ORANGE}15`, color: i.quantity === 0 ? SYS_RED : SYS_ORANGE, flexShrink: 0 }}>
                        {i.quantity === 0 ? 'Out' : 'Low'}
                      </span>
                    </div>
                  ))}
                  {lowStock.length > 6 && (
                    <button onClick={() => setTab('products')} style={{ marginTop: 10, fontSize: 12, color: SYS_BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT }}>
                      View all {lowStock.length} alerts →
                    </button>
                  )}
                </div>
              )}

              {/* Category value breakdown + Top items */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
                {/* Category value breakdown */}
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: INK, margin: '0 0 16px' }}>Value by Category</h3>
                  {catValueList.length === 0
                    ? <p style={{ color: MUTED, fontSize: 13 }}>No inventory yet.</p>
                    : catValueList.map(([cat, v]) => (
                      <div key={cat} style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                          <div>
                            <span style={{ fontSize: 13, color: SECONDARY, fontWeight: 500 }}>{cat}</span>
                            <span style={{ fontSize: 11, color: MUTED, marginLeft: 6 }}>{v.count} SKU{v.count !== 1 ? 's' : ''}</span>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: SYS_GREEN }}>{fmt(v.retail)}</span>
                        </div>
                        <div style={{ height: 4, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: BRAND, width: `${maxCatRetail > 0 ? (v.retail / maxCatRetail) * 100 : 0}%`, borderRadius: 2, transition: 'width 0.5s' }} />
                        </div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>Cost: {fmt(v.cost)} · Margin: {v.retail > 0 ? (((v.retail - v.cost) / v.retail) * 100).toFixed(0) : 0}%</div>
                      </div>
                    ))
                  }
                </div>

                {/* Top items by retail value */}
                <div style={{ ...CARD, padding: '20px 22px' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: INK, margin: '0 0 16px' }}>Top Items by Value</h3>
                  {topItems.length === 0
                    ? <p style={{ color: MUTED, fontSize: 13 }}>No items with prices set.</p>
                    : topItems.map((item, i) => {
                      const totalVal = item.quantity * item.sellingPrice;
                      return (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
                          <span style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#FEF3C7' : BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: i === 0 ? SYS_ORANGE : MUTED, flexShrink: 0 }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontSize: 12, color: SECONDARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>{item.name}{item.side ? ` (${item.side})` : ''}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: SYS_BLUE, flexShrink: 0 }}>{fmt(totalVal)}</span>
                            </div>
                            <div style={{ height: 3, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', background: i === 0 ? SYS_ORANGE : SYS_BLUE, width: `${topItems[0] ? (totalVal / (topItems[0].quantity * topItems[0].sellingPrice)) * 100 : 0}%`, borderRadius: 2 }} />
                            </div>
                            <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{item.quantity} units × {fmt(item.sellingPrice)}</div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>

              {/* Category item count chips */}
              <div style={{ ...CARD, padding: '18px 22px' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: INK, margin: '0 0 14px' }}>Items per Category</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {Object.entries(catBreakdown).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                    <button
                      key={cat}
                      onClick={() => { setTab('products'); setCatFilter(cat); }}
                      style={{ background: BG, border: 'none', borderRadius: 12, padding: '10px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', fontFamily: FONT, minWidth: 80 }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 700, color: BRAND }}>{count}</div>
                      <div style={{ fontSize: 11, color: SECONDARY, marginTop: 2, textAlign: 'center' }}>{cat}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════
            PRODUCTS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'products' && (
          <div>
            {/* ── Toolbar ── */}
            <div style={{ marginBottom: 14 }}>
              {/* Search bar */}
              <div style={{ ...CARD, borderRadius: 12, display: 'flex', alignItems: 'center', padding: '0 14px', marginBottom: 12, gap: 10 }}>
                <Search size={15} color={MUTED} style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search by name or side…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ flex: 1, padding: '12px 0', border: 'none', outline: 'none', fontSize: 15, color: INK, background: 'transparent', fontFamily: FONT }}
                />
                {search && (
                  <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, display: 'flex', padding: 0 }}>
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Filter row */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Stock filter pills */}
                {[
                  { val: 'all', label: 'All Stock' },
                  { val: 'in-stock', label: 'In Stock' },
                  { val: 'low-stock', label: 'Low Stock' },
                  { val: 'out-of-stock', label: 'Out of Stock' },
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => setStockFilter(opt.val)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: 20,
                      border: 'none',
                      background: stockFilter === opt.val ? BRAND : 'white',
                      color: stockFilter === opt.val ? 'white' : SECONDARY,
                      fontWeight: stockFilter === opt.val ? 600 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: FONT,
                      boxShadow: stockFilter === opt.val ? 'none' : '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.05)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}

                <div style={{ flex: 1 }} />

                {/* Add Item button */}
                <button
                  onClick={() => setItemModal('new')}
                  style={{ padding: '9px 18px', background: BRAND, border: 'none', borderRadius: 12, color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontFamily: FONT, whiteSpace: 'nowrap' }}
                >
                  <Plus size={15} /> Add Item
                </button>
              </div>

              {/* Category pills */}
              <div style={{ display: 'flex', gap: 7, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCatFilter(cat)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      border: 'none',
                      background: catFilter === cat ? BRAND : 'white',
                      color: catFilter === cat ? 'white' : SECONDARY,
                      fontWeight: catFilter === cat ? 600 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: FONT,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      boxShadow: catFilter === cat ? 'none' : '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.05)',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Mobile cards ── */}
            {isMobile ? (
              <div>
                {filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: MUTED, background: 'white', borderRadius: 18, ...CARD }}>No items found.</div>
                ) : groupByCategory(filtered).map(([category, items]) => (
                  <div key={category}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 2px 10px', marginTop: 6 }}>
                      {category} <span style={{ color: MUTED, fontWeight: 500, opacity: 0.7 }}>({items.length})</span>
                    </div>
                    {items.map(item => {
                      const isOut = item.quantity === 0;
                      const isLow = !isOut && item.minQuantity > 0 && item.quantity <= item.minQuantity;
                      const actorName = item.updatedByName || item.createdByName;
                      const actorDate = item.lastUpdated || item.createdAt;
                      const isCreate = !item.updatedByName && !!item.createdByName;
                      return (
                        <div key={item.id} style={{
                          background: 'white',
                          borderRadius: 14,
                          marginBottom: 10,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)',
                          padding: '14px 16px',
                        }}>
                          {/* Name row */}
                          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                            {item.imagePath && (
                              <div style={{ width: 52, height: 52, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: BG }}>
                                <img src={item.imagePath} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 4 }}>{item.name}</div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {item.side && <span style={{ fontSize: 11, background: `${BRAND}18`, color: BRAND, borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>{item.side}</span>}
                                <span style={{ fontSize: 11, background: BG, color: SECONDARY, borderRadius: 6, padding: '2px 7px' }}>{item.category}</span>
                                {isOut && <span style={{ fontSize: 11, background: `${SYS_RED}15`, color: SYS_RED, borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>Out of Stock</span>}
                                {isLow && <span style={{ fontSize: 11, background: `${SYS_ORANGE}15`, color: SYS_ORANGE, borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>Low Stock</span>}
                              </div>
                            </div>
                          </div>

                          {/* Stats grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 12px', marginBottom: 12 }}>
                            <div>
                              <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Qty</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontWeight: 700, fontSize: 14, color: isOut ? SYS_RED : isLow ? SYS_ORANGE : INK }}>{item.quantity}</span>
                                <button onClick={() => setStockModal(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND, padding: 0, display: 'flex' }} title="Adjust stock">
                                  <ChevronUp size={14} />
                                </button>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Sell</div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: INK }}>{fmt(item.sellingPrice)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Cost</div>
                              <div style={{ fontSize: 13, color: MUTED }}>{fmt(item.costPrice)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Min</div>
                              <div style={{ fontSize: 13, color: MUTED }}>{item.minQuantity}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Supplier</div>
                              <div style={{ fontSize: 12, color: SECONDARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.supplier || '—'}</div>
                            </div>
                            {actorName && (
                              <div>
                                <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Updated By</div>
                                <div style={{ fontSize: 12, color: SECONDARY, fontWeight: 500 }}>{displayName(actorName)}</div>
                                <div style={{ fontSize: 10, color: MUTED }}>{isCreate ? 'Added ' : 'Edited '}{fmtDate(actorDate)}</div>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${BG}`, paddingTop: 10 }}>
                            <button onClick={() => setItemModal(item)} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: BG, cursor: 'pointer', color: SECONDARY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 13, fontWeight: 500, fontFamily: FONT }}>
                              <Edit2 size={13} /> Edit
                            </button>
                            <button onClick={() => handleDeleteItem(item)} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: `${SYS_RED}10`, cursor: 'pointer', color: SYS_RED, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 13, fontWeight: 500, fontFamily: FONT }}>
                              <Trash2 size={13} /> Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div style={{ fontSize: 12, color: MUTED, textAlign: 'center', padding: '4px 0 8px', fontFamily: FONT }}>
                  Showing {filtered.length} of {inventory.length} items
                </div>
              </div>
            ) : (
              /* ── Desktop table ── */
              <div style={{ ...CARD, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: BG }}>
                      {['Side', 'Name', 'Category', 'Qty', 'Min', 'Cost', 'Sell', 'Supplier', 'Location', 'Updated By', 'Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '11px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: MUTED, fontFamily: FONT }}>No items found.</td></tr>
                    ) : groupByCategory(filtered).map(([category, items]) => (
                      <React.Fragment key={category}>
                        {/* Category section header */}
                        <tr>
                          <td colSpan={11} style={{ padding: '9px 12px', background: BG }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{category}</span>
                            <span style={{ fontSize: 11, color: MUTED, marginLeft: 7, opacity: 0.7 }}>({items.length})</span>
                          </td>
                        </tr>
                        {items.map(item => {
                          const isOut = item.quantity === 0;
                          const isLow = !isOut && item.minQuantity > 0 && item.quantity <= item.minQuantity;
                          return (
                            <tr key={item.id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{item.side || '-'}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div style={{ width: 36, height: 36, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {item.imagePath
                                      ? <img src={item.imagePath} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      : <span style={{ fontSize: 16 }}>📦</span>
                                    }
                                  </div>
                                  <span style={{ fontWeight: 500, color: INK }}>{item.name}</span>
                                </div>
                              </td>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{item.category}</td>
                              <td style={{ padding: '10px 12px', fontWeight: 700, color: isOut ? SYS_RED : isLow ? SYS_ORANGE : INK }}>
                                {item.quantity}
                                <span style={{ marginLeft: 4 }}>
                                  <button onClick={() => setStockModal(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND, padding: '0 2px' }} title="Adjust stock">
                                    <ChevronUp size={12} />
                                  </button>
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{item.minQuantity}</td>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{fmt(item.costPrice)}</td>
                              <td style={{ padding: '10px 12px', color: SECONDARY, fontWeight: 500 }}>{fmt(item.sellingPrice)}</td>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{item.supplier || '-'}</td>
                              <td style={{ padding: '10px 12px', color: MUTED }}>{item.location || '-'}</td>
                              <ActivityCell item={item} />
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button onClick={() => setItemModal(item)} style={{ padding: '5px 10px', border: 'none', borderRadius: 8, background: BG, cursor: 'pointer', color: SECONDARY, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, fontFamily: FONT }}>
                                    <Edit2 size={11} /> Edit
                                  </button>
                                  <button onClick={() => handleDeleteItem(item)} style={{ padding: '5px 10px', border: 'none', borderRadius: 8, background: `${SYS_RED}10`, cursor: 'pointer', color: SYS_RED, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, fontFamily: FONT }}>
                                    <Trash2 size={11} /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: '10px 14px', background: BG, fontSize: 12, color: MUTED, fontFamily: FONT }}>
                  Showing {filtered.length} of {inventory.length} items
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SUPPLIERS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'suppliers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                onClick={() => setSupplierModal('new')}
                style={{ padding: '9px 18px', background: BRAND, border: 'none', borderRadius: 12, color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontFamily: FONT }}
              >
                <Plus size={15} /> Add Supplier
              </button>
            </div>

            {suppliers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: MUTED, fontFamily: FONT, ...CARD }}>No suppliers yet.</div>
            ) : (
              <div style={{ ...CARD, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: BG }}>
                      {['Company', 'Contact Person', 'Email', 'Phone', 'Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '11px 14px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, color: INK }}>{s.name}</td>
                        <td style={{ padding: '12px 14px', color: SECONDARY }}>{s.contactPerson || '-'}</td>
                        <td style={{ padding: '12px 14px', color: MUTED }}>{s.email || '-'}</td>
                        <td style={{ padding: '12px 14px', color: MUTED }}>{s.phone || '-'}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: 7 }}>
                            <button onClick={() => setSupplierModal(s)} style={{ padding: '5px 12px', border: 'none', borderRadius: 8, background: BG, cursor: 'pointer', color: SECONDARY, fontSize: 12, fontWeight: 500, fontFamily: FONT }}>Edit</button>
                            <button onClick={() => handleDeleteSupplier(s)} style={{ padding: '5px 12px', border: 'none', borderRadius: 8, background: `${SYS_RED}10`, cursor: 'pointer', color: SYS_RED, fontSize: 12, fontWeight: 500, fontFamily: FONT }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {itemModal && (
        <ItemModal
          item={itemModal === 'new' ? null : itemModal}
          suppliers={suppliers}
          onSave={handleSaveItem}
          onClose={() => setItemModal(null)}
        />
      )}
      {stockModal && (
        <StockAdjustModal
          item={stockModal}
          onAdjust={(change, reason) => handleAdjustStock(stockModal, change, reason)}
          onClose={() => setStockModal(null)}
        />
      )}
      {supplierModal && (
        <SupplierModal
          supplier={supplierModal === 'new' ? null : supplierModal}
          onSave={handleSaveSupplier}
          onClose={() => setSupplierModal(null)}
        />
      )}
    </div>
  );
}
